import { useState } from "react";
import { formatDisplayDate } from "../utils/dateFormat";
import DateInputField from "./DateInputField";

function ReservasList({
  reservas,
  canchas,
  canchaItems,
  fechaFiltro,
  fechaHoy,
  soloLectura = false,
  onFechaFiltroChange,
  onCancel,
  onDelete,
  onMarkPagada,
  onUpdate,
  canDeleteDefinitivo = false,
}) {
  const horasDisponibles = Array.from({ length: 48 }, (_, index) => {
    const hour = String(Math.floor(index / 2)).padStart(2, "0");
    const minute = index % 2 === 0 ? "00" : "30";
    return `${hour}:${minute}`;
  });
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState({
    cliente: "",
    cancha: "",
    fecha: "",
    hora: "",
    duracionMin: 60,
    monto: "",
    pagado: false,
  });

  const startEdit = (reserva) => {
    setEditandoId(reserva.id);
    setForm({
      cliente: reserva.cliente,
      cancha: reserva.cancha,
      fecha: reserva.fecha,
      hora: reserva.hora,
      duracionMin: reserva.duracion_min || 60,
      monto: reserva.monto,
      pagado: reserva.pagado,
    });
  };

  const cancelEdit = () => {
    setEditandoId(null);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => {
      const nextValue = type === "checkbox" ? checked : value;
      const nextForm = {
        ...prev,
        [name]: nextValue,
      };

      if (name === "cancha" || name === "duracionMin") {
        const canchaSeleccionada = (canchaItems || []).find((item) => item.nombre === nextForm.cancha);
        const valorHora = Number(canchaSeleccionada?.valor_hora || 0);
        if (valorHora > 0) {
          const montoCalculado = (valorHora * Number(nextForm.duracionMin || 60)) / 60;
          nextForm.monto = montoCalculado.toFixed(2);
        }
      }

      return nextForm;
    });
  };

  const saveEdit = async (reservaId) => {
    try {
      await onUpdate(reservaId, form);
      setEditandoId(null);
    } catch (error) {
      return;
    }
  };

  const setDuracion = (value) => {
    setForm((prev) => ({
      ...prev,
      duracionMin: value,
    }));
  };

  const changeDuracion = (delta) => {
    const actual = Number(form.duracionMin || 60);
    const siguiente = Math.max(60, Math.min(180, actual + delta));
    setDuracion(siguiente);
  };

  const formatDuracion = (min) => {
    const minutos = Number(min);
    if (minutos === 60) {
      return "1 hora";
    }
    if (minutos % 60 === 0) {
      const horas = minutos / 60;
      return `${horas} horas`;
    }
    const horasEnteras = Math.floor(minutos / 60);
    return horasEnteras === 1 ? "1 hora y media" : `${horasEnteras} horas y media`;
  };

  return (
    <article className="panel">
      <div className="section-title">
        <h2>Reservas registradas</h2>
        <span>{reservas.length} elementos</span>
      </div>
      <div className="report-filter-grid" style={{ marginBottom: 12 }}>
        <label>
          <span>Fecha de consulta</span>
          <DateInputField value={fechaFiltro} onChange={onFechaFiltroChange} ariaLabel="Fecha de consulta" />
        </label>
      </div>
      <p className={soloLectura ? "status-warn" : "status-ok"} style={{ marginTop: 0 }}>
        {soloLectura
          ? `Mostrando historial de ${formatDisplayDate(fechaFiltro)} en solo lectura.`
          : `Mostrando reservas operativas del día de hoy (${formatDisplayDate(fechaHoy)}).`}
      </p>
      <div className="list-stack">
        {reservas.length === 0 ? <p>No hay reservas registradas.</p> : null}
        {reservas.map((reserva) => (
          <div className="list-item" key={reserva.id}>
            {editandoId === reserva.id ? (
              <div className="edit-grid">
                <label>
                  <span className="visually-hidden">Cliente</span>
                  <input
                    name="cliente"
                    value={form.cliente}
                    onChange={handleChange}
                    placeholder="Cliente"
                    aria-label="Cliente"
                  />
                </label>
                <label>
                  <span className="visually-hidden">Cancha</span>
                  <select name="cancha" value={form.cancha} onChange={handleChange} aria-label="Cancha">
                    <option value="">Cancha</option>
                    {canchas.map((cancha) => (
                      <option key={cancha} value={cancha}>
                        {cancha}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="visually-hidden">Fecha</span>
                  <DateInputField name="fecha" value={form.fecha} onChange={handleChange} ariaLabel="Fecha" />
                </label>
                <label>
                  <span className="visually-hidden">Hora</span>
                  <select name="hora" value={form.hora} onChange={handleChange} aria-label="Hora">
                    <option value="" disabled>Hora (24h)</option>
                    {form.hora && !horasDisponibles.includes(form.hora) ? (
                      <option value={form.hora}>{form.hora}</option>
                    ) : null}
                    {horasDisponibles.map((hora) => (
                      <option key={hora} value={hora}>{hora}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span className="visually-hidden">Duración</span>
                  <div className="duration-choice-row">
                    <div className="duration-stepper">
                      <button type="button" className="duration-step" onClick={() => changeDuracion(-30)}>
                        -
                      </button>
                      <span className="duration-value">{formatDuracion(form.duracionMin)}</span>
                      <button type="button" className="duration-step" onClick={() => changeDuracion(30)}>
                        +
                      </button>
                    </div>
                  </div>
                </label>
                <label>
                  <span className="visually-hidden">Monto</span>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    name="monto"
                    value={form.monto}
                    onChange={handleChange}
                    placeholder="Monto"
                    aria-label="Monto"
                  />
                </label>
                <label className="checkbox-inline">
                  <input type="checkbox" name="pagado" checked={form.pagado} onChange={handleChange} />
                  Pagado
                </label>
                <div className="item-actions inline-actions">
                  <button type="button" className="secondary-button" onClick={() => saveEdit(reserva.id)}>
                    Guardar
                  </button>
                  <button type="button" className="secondary-button" onClick={cancelEdit}>
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <>
                <strong>{reserva.cliente}</strong>
                <span>{reserva.cancha}</span>
                <span>{formatDisplayDate(reserva.fecha)} a las {reserva.hora}</span>
                <span>Duración: {formatDuracion(reserva.duracion_min || 60)}</span>
                <span>${Number(reserva.monto).toFixed(2)}</span>
                <span className={reserva.tipo === "interna" ? "status-info" : "status-default"}>
                  {reserva.tipo === "interna" ? "Escuela (histórico)" : "Comercial"}
                </span>
                <span className={reserva.pagado ? "status-ok" : "status-warn"}>{reserva.pagado ? "Pagado" : "Pendiente"}</span>
                <span className={reserva.estado === "cancelada" ? "status-danger" : "status-ok"}>
                  {reserva.estado === "cancelada" ? "Cancelada" : "Confirmada"}
                </span>
                <label className="checkbox-inline">
                  <input
                    type="checkbox"
                    checked={reserva.pagado}
                    disabled={reserva.pagado || reserva.estado === "cancelada" || soloLectura}
                    onChange={() => onMarkPagada(reserva.id)}
                  />
                  Marcar como pagada
                </label>
                <div className="item-actions inline-actions">
                  <button
                    type="button"
                    className="secondary-button edit-action"
                    onClick={() => startEdit(reserva)}
                    disabled={reserva.estado === "cancelada" || soloLectura}
                  >
                    Editar reserva
                  </button>
                  <button
                    type="button"
                    className="secondary-button danger"
                    onClick={() => onCancel(reserva.id)}
                    disabled={reserva.estado === "cancelada" || soloLectura}
                  >
                    Cancelar reserva
                  </button>
                  {reserva.estado === "cancelada" && canDeleteDefinitivo && !soloLectura ? (
                    <button
                      type="button"
                      className="secondary-button danger"
                      onClick={() => onDelete(reserva)}
                    >
                      Eliminar definitivamente
                    </button>
                  ) : null}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </article>
  );
}


export default ReservasList;